import userResources from "api/userResources";
import ScrollToTopOnRouteChange from "components/ScrollToTopOnRouteChange/ScrollToTopOnRouteChange";
import UserNavbar from "components/UserNavbar/UserNavbar";
import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  FaBriefcase,
  FaUserTie,
  FaUsers,
  FaGraduationCap,
  FaTasks,
  FaChartLine,
  FaFileUpload,
  FaCheckCircle,
  FaLanguage,
  FaClock,
} from "react-icons/fa";
import {
  MdWork,
  MdSchool,
  MdPerson,
  MdBusinessCenter,
  MdStars,
  MdError
} from "react-icons/md";

export default function Job() {
  const [agentResume, setAgentResume] = useState(null);
  const [managerResume, setManagerResume] = useState(null);
  const [agentError, setAgentError] = useState(false);
  const [managerError, setManagerError] = useState(false);
  const [activeTab, setActiveTab] = useState("agent");

  const handleAgentResumeUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      setAgentResume(file);
      setAgentError(false);
      toast.info("Agent resume selected");
    } else {
      toast.error("Please select a PDF file");
    }
  };

  const handleManagerResumeUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      setManagerResume(file);
      setManagerError(false);
      toast.info("Manager resume selected");
    } else {
      toast.error("Please select a PDF file");
    }
  };

  const handleAgentSubmit = async (event) => {
    event.preventDefault();
    if (!agentResume) {
      setAgentError(true);
      toast.error("Please select a resume file");
      return;
    }
    
    try {
      toast.info("Submitting application...");
      const response = await userResources.agent(agentResume);
      console.log(response);
      toast.success("Agent application submitted successfully!");
      setAgentResume(null);
      event.target.reset();
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    }
  };

  const handleManagerSubmit = async (event) => {
    event.preventDefault();
    if (!managerResume) {
      setManagerError(true);
      toast.error("Please select a resume file");
      return;
    }
    
    try {
      toast.info("Submitting application...");
      const response = await userResources.manager(managerResume);
      console.log(response);
      toast.success("Manager application submitted successfully!");
      setManagerResume(null);
      event.target.reset();
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    }
  };

  return (
    <section className="min-h-screen bg-gradient-to-b from-gray-50 to-blue-50">
      <UserNavbar />
      <ScrollToTopOnRouteChange />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="md:w-2/3">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
                  <FaBriefcase className="w-10 h-10" />
                </div>
                <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
                  <MdWork className="w-10 h-10" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Join Our Team
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl">
                Exciting career opportunities await at Maharaja Flights® Limited. 
                Be part of a dynamic team shaping the future of travel.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm flex items-center gap-2">
                  <FaCheckCircle className="w-4 h-4" /> Competitive Salary
                </span>
                <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm flex items-center gap-2">
                  <FaCheckCircle className="w-4 h-4" /> Training Provided
                </span>
                <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm flex items-center gap-2">
                  <FaCheckCircle className="w-4 h-4" /> Career Growth
                </span>
              </div>
            </div>
            <div className="md:w-1/3">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold mb-4">Quick Apply</h3>
                <p className="text-blue-100 text-sm mb-4">
                  Ready to join our team? Upload your resume and we'll contact you.
                </p>
                <div className="space-y-3">
                  <button 
                    onClick={() => setActiveTab("agent")}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      activeTab === "agent" ? "bg-white text-blue-600" : "bg-white/20 text-white hover:bg-white/30"
                    }`}
                  >
                    Apply for Agent Position
                  </button>
                  <button 
                    onClick={() => setActiveTab("manager")}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      activeTab === "manager" ? "bg-white text-blue-600" : "bg-white/20 text-white hover:bg-white/30"
                    }`}
                  >
                    Apply for Manager Position
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex space-x-2 mb-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("agent")}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
              activeTab === "agent"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <FaUserTie className="w-5 h-5" />
            Travel Agent
          </button>
          <button
            onClick={() => setActiveTab("manager")}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
              activeTab === "manager"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <FaUsers className="w-5 h-5" />
            Manager
          </button>
        </div>

        {/* Agent Position */}
        {activeTab === "agent" && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <FaUserTie className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-2">Travel Agent</h2>
                    <p className="text-blue-100">Full-time • Entry to Mid-level</p>
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="text-white font-semibold">Training Provided</span>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Left Column - Qualifications */}
                <div className="lg:col-span-2">
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <MdSchool className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">Qualifications</h3>
                    </div>
                    <div className="space-y-4">
                      {[
                        "1 year sales experience in Travel industry would be an asset",
                        "Fluent in spoken and written English",
                        "Knowledge of Hindi and any other Indian language is a strong asset",
                        "Strong communication skills and market awareness",
                        "Exceptional customer service skills",
                        "Proficiency working with Excel",
                        "Proficiency with Gmail, Google Docs, and Google Calendar"
                      ].map((item, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <FaCheckCircle className="w-3 h-3 text-green-600" />
                          </div>
                          <span className="text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-purple-100">
                        <FaTasks className="w-6 h-6 text-purple-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">Responsibilities</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {[
                        "Handle customer inquiries and provide assistance",
                        "Book flights, hotels, and other travel arrangements",
                        "Provide travel information and advice to clients",
                        "Process reservations and payments",
                        "Resolve customer complaints and issues",
                        "Keep updated on travel industry trends"
                      ].map((item, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span className="font-medium text-gray-900">{item}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column - Application Form */}
                <div className="lg:col-span-1">
                  <div className="bg-gradient-to-b from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <FaFileUpload className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Apply Now</h3>
                    </div>

                    <form onSubmit={handleAgentSubmit}>
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Upload Resume (PDF only)
                        </label>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={handleAgentResumeUpload}
                            className="hidden"
                            id="agent-resume"
                          />
                          <label
                            htmlFor="agent-resume"
                            className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                              agentError
                                ? "border-red-300 bg-red-50"
                                : agentResume
                                ? "border-green-300 bg-green-50"
                                : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                            }`}
                          >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              {agentResume ? (
                                <>
                                  <FaCheckCircle className="w-12 h-12 text-green-500 mb-3" />
                                  <p className="text-sm text-gray-700 font-medium">
                                    {agentResume.name}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Click to change file
                                  </p>
                                </>
                              ) : (
                                <>
                                  <FaFileUpload className="w-12 h-12 text-gray-400 mb-3" />
                                  <p className="text-sm text-gray-600">
                                    <span className="font-medium">Click to upload</span> or drag and drop
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">PDF up to 10MB</p>
                                </>
                              )}
                            </div>
                          </label>
                          {agentError && (
                            <div className="flex items-center gap-2 mt-2 text-red-600">
                              <MdError className="w-5 h-5" />
                              <span className="text-sm">Please select a resume file</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <button
                          type="submit"
                          disabled={!agentResume}
                          className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                            agentResume
                              ? "bg-blue-600 hover:bg-blue-700 text-white transform hover:-translate-y-0.5"
                              : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          Submit Application
                        </button>
                        
                        <div className="text-center">
                          <p className="text-sm text-gray-600">
                            We'll review your application and contact you within 5-7 business days
                          </p>
                        </div>
                      </div>
                    </form>

                    {/* Quick Info */}
                    <div className="mt-8 pt-6 border-t border-blue-200">
                      <h4 className="font-semibold text-gray-900 mb-3">What to Expect</h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <FaClock className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-gray-600">Response within 1 week</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <FaLanguage className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-gray-600">English + Hindi preferred</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <FaGraduationCap className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-gray-600">Training provided for freshers</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manager Position */}
        {activeTab === "manager" && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <FaUsers className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-2">Manager</h2>
                    <p className="text-indigo-100">Full-time • Management Level</p>
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="text-white font-semibold">TICO Certification Required</span>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Left Column - Qualifications & Responsibilities */}
                <div className="lg:col-span-2">
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-indigo-100">
                        <MdBusinessCenter className="w-6 h-6 text-indigo-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">Qualifications</h3>
                    </div>
                    <div className="space-y-4">
                      {[
                        "College/University Degree",
                        "Minimum 2 years in Travel trade or equivalent experience",
                        "Must have TICO Certification for Supervisor/Manager"
                      ].map((item, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <FaCheckCircle className="w-3 h-3 text-green-600" />
                          </div>
                          <span className="text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-purple-100">
                        <FaChartLine className="w-6 h-6 text-purple-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">Responsibilities</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {[
                        "Manage staff and assign duties",
                        "Develop and implement marketing strategies",
                        "Plan budgets and monitor revenues",
                        "Determine staffing requirements",
                        "Resolve customer complaints",
                        "Plan, organize, and evaluate daily operations",
                        "Recruit, hire and supervise staff",
                        "Study market research and trends"
                      ].map((item, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                            <span className="font-medium text-gray-900">{item}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-pink-100">
                        <MdPerson className="w-6 h-6 text-pink-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">Personal Suitability</h3>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {[
                        "Adaptability",
                        "Analytical",
                        "Collaborative",
                        "Efficiency",
                        "Goal-oriented",
                        "Excellent communication",
                        "Judgement",
                        "Organized"
                      ].map((trait, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-2 rounded-full bg-indigo-50 text-indigo-700 text-sm font-medium"
                        >
                          <MdStars className="w-4 h-4 mr-2" />
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column - Application Form */}
                <div className="lg:col-span-1">
                  <div className="bg-gradient-to-b from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 rounded-lg bg-indigo-100">
                        <FaFileUpload className="w-6 h-6 text-indigo-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Apply Now</h3>
                    </div>

                    <form onSubmit={handleManagerSubmit}>
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Upload Resume (PDF only)
                        </label>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={handleManagerResumeUpload}
                            className="hidden"
                            id="manager-resume"
                          />
                          <label
                            htmlFor="manager-resume"
                            className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                              managerError
                                ? "border-red-300 bg-red-50"
                                : managerResume
                                ? "border-green-300 bg-green-50"
                                : "border-gray-300 hover:border-indigo-400 hover:bg-indigo-50"
                            }`}
                          >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              {managerResume ? (
                                <>
                                  <FaCheckCircle className="w-12 h-12 text-green-500 mb-3" />
                                  <p className="text-sm text-gray-700 font-medium">
                                    {managerResume.name}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Click to change file
                                  </p>
                                </>
                              ) : (
                                <>
                                  <FaFileUpload className="w-12 h-12 text-gray-400 mb-3" />
                                  <p className="text-sm text-gray-600">
                                    <span className="font-medium">Click to upload</span> or drag and drop
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">PDF up to 10MB</p>
                                </>
                              )}
                            </div>
                          </label>
                          {managerError && (
                            <div className="flex items-center gap-2 mt-2 text-red-600">
                              <MdError className="w-5 h-5" />
                              <span className="text-sm">Please select a resume file</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <button
                          type="submit"
                          disabled={!managerResume}
                          className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                            managerResume
                              ? "bg-indigo-600 hover:bg-indigo-700 text-white transform hover:-translate-y-0.5"
                              : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          Submit Application
                        </button>
                        
                        <div className="text-center">
                          <p className="text-sm text-gray-600">
                            We'll contact selected candidates for interviews within 7-10 business days
                          </p>
                        </div>
                      </div>
                    </form>

                    {/* Job Details */}
                    <div className="mt-8 pt-6 border-t border-indigo-200">
                      <h4 className="font-semibold text-gray-900 mb-3">Job Details</h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <MdWork className="w-4 h-4 text-indigo-600" />
                          <span className="text-sm text-gray-600">Work Term: Permanent</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <FaLanguage className="w-4 h-4 text-indigo-600" />
                          <span className="text-sm text-gray-600">English required, Hindi preferred</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <MdStars className="w-4 h-4 text-indigo-600" />
                          <span className="text-sm text-gray-600">Leadership role with growth potential</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact Section */}
        <div className="mt-12 bg-gradient-to-r from-gray-900 to-blue-900 rounded-2xl p-8 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="md:w-2/3">
              <h3 className="text-2xl font-bold mb-4">Questions About Careers?</h3>
              <p className="text-blue-100">
                Contact our HR department for more information about job opportunities, 
                application process, or company culture.
              </p>
            </div>
            <div className="md:w-1/3">
              <div className="space-y-4">
                <button className="w-full bg-white text-blue-600 hover:bg-blue-50 font-semibold py-3 px-6 rounded-lg transition-colors">
                  Contact HR Department
                </button>
                <p className="text-sm text-blue-200 text-center">
                  Email: careers@maharajaflights.com
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Why Join Maharaja Flights?
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                icon: <FaChartLine className="w-8 h-8" />,
                title: "Career Growth",
                description: "Clear progression paths and promotion opportunities"
              },
              {
                icon: <FaGraduationCap className="w-8 h-8" />,
                title: "Training & Development",
                description: "Continuous learning and skill enhancement programs"
              },
              {
                icon: <MdStars className="w-8 h-8" />,
                title: "Competitive Benefits",
                description: "Comprehensive health and wellness packages"
              },
              {
                icon: <FaUsers className="w-8 h-8" />,
                title: "Team Environment",
                description: "Supportive, collaborative workplace culture"
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:border-blue-300 transition-colors">
                <div className="text-blue-600 mb-4">{benefit.icon}</div>
                <h4 className="font-semibold text-gray-900 mb-2">{benefit.title}</h4>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}